<template>
    <div class="pagination display--grid" role="naviagation" :aria-label="$t('accessibility.pagination')">
        <div class="pagination__results">{{ `${ pagination.range[0] } - ${ pagination.range[1] } de ${ pagination.total }` }}</div>
        <div class="pagination__navigation display--flex">
            <icon-double-arrow
                @click.native="navigate({ orientation: 'first' })"
                class="pagination__arrow__button left pointer"
                :class="{ 'disable': !activeLeftDoubleArrow }"
            />

            <icon-arrow
                @click.native="navigate({ orientation: 'previous' })"
                class="pagination__arrow__button left pointer"
                :class="{ 'disable': !activeLeftArrow }"
            />
            <div class="pagination__pages display--flex">
                <span
                    v-for="page in createPaginationArray( pagination.current_page, pagination.total_pages )"
                    :key="`page-${page}`"
                    class="pagination__pages__page display--inline-b pointer"
                    :class="{ 'pagination__pages__page--active': pagination.current_page === page }"
                    @click="navigate({ page })"
                >
                    {{ page }}
                </span>
            </div>
            <icon-arrow
                @click.native="navigate({ orientation: 'next' })"
                class="pagination__arrow__button pointer"
                :class="{ 'disable': !activeRightArrow }"
            />
            <icon-double-arrow
                @click.native="navigate({ orientation: 'last' })"
                class="pagination__arrow__button pointer"
                :class="{ 'disable': !activeRightDoubleArrow }"
            />
        </div>
    </div>
</template>

<script>
// packages
import { reactive, toRefs, watch } from '@vue/composition-api';

export default {
    props: {
        pagination: Object
    },
    components: {
        IconDoubleArrow: () => import( /* webpackChunkName: 'IconDoubleArrow' */ '@/components/bundles/layout/images/icons/IconDoubleArrow' ),
        IconArrow: () => import( /* webpackChunkName: 'IconArrow' */ '@/components/bundles/layout/images/icons/IconArrow' )
    },
    setup ( props, { root: { $router, $route } }) {
        const state = reactive({
            activeLeftArrow: false,
            activeRightArrow: false,
            activeLeftDoubleArrow: false,
            activeRightDoubleArrow: false
        });

        watch( () => props.pagination,
            newPagination => {
                // se a nova paginação for nula,
                // não executa o resto do código.
                if ( !newPagination )
                    return;

                // verificação se a página actual é superior a primeira página.
                // eslint-disable-next-line
                state.activeLeftArrow = state.activeLeftDoubleArrow = newPagination.current_page > 1;

                // verificação se a página actual é inferior ao total de páginas.
                // eslint-disable-next-line
                state.activeRightArrow = state.activeRightDoubleArrow = newPagination.current_page < newPagination.total_pages;
            }, {
                immediate: true
            });

        const createPaginationArray = ( currentPage, totalPages ) => {
            const pagesArray = [];
            const pagesInPagination = 7; // Número de páginas que irá aparecer na paginação
            let startPage = 1;
            let endPage = totalPages;

            // Cálculo da primeira e última página que irá aparecer na paginação --> startPAge e endPage
            // Se o total for maior que 7:
            // -- Se a página atual for menor que 5
            // ---- startPage é 1
            // ---- endPage é o nº de páginas que irão aparecer na paginação
            // -- Senão se a página atual for maior ou igual a 5 e menor ou igual a total - 3
            // ---- startPage é a página atual - 3
            // ---- endPage é a página atual - 3
            // -- Senão, startPage é totalPages - pagesInPagination + 1
            if ( totalPages > pagesInPagination ) {
                if ( currentPage < 5 ) {
                    startPage = 1;
                    endPage = pagesInPagination;
                } else if ( currentPage >= 5 && currentPage <= totalPages - 3 ) {
                    startPage = currentPage - 3;
                    endPage = currentPage + 3;
                } else
                    startPage = totalPages - pagesInPagination + 1;
            }

            for ( let i = startPage; i <= endPage; i++ )
                pagesArray.push( i );

            return pagesArray;
        };

        const navigate = ({ orientation, page }) => {
            let newPage;

            if ( page )
                newPage = page;
            else {
                const orientations = {
                    next: props.pagination.current_page + 1,
                    previous: props.pagination.current_page - 1,
                    first: 1,
                    last: props.pagination.total_pages
                };

                // validação de qual é a orientação.
                newPage = orientations[orientation];
            }

            // construção da query.
            const query = { ...$route.query, ...{ page: newPage } };

            $router.push({ query });
        };

        return {
            ...toRefs( state ),
            navigate,
            createPaginationArray
        };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Variables
    // =============================================================================
    $arrowClass: '.pagination__arrow';

    // =============================================================================
    // Style
    // =============================================================================
    .pagination {
        grid-template: auto / 150px 1fr 150px;
        @include position-grid( center, center );
        grid-gap: 20px;
        margin-top: 130px;
        &__results {
            @include typography-specs( 14px, 26px, 300 );
            color: $color-grey;
        }
        &__navigation {
            @include position-grid( center, center );
            gap: 14px;
        }
        &__pages {
            line-height: 29px;
            @include  elements-colors ( transparent, $color-grey );
            align-items: center;
            &__page {
                line-height: 24px;
                text-align: center;
                @include rounded-corner( 5px );
                @include dimensions( auto, 24px );
                min-width: 24px;
                padding: 0 5px;
                &--active {
                    @include  elements-colors ( $color-blue, $color-white );
                }
            }
        }
        &__arrow {
            @include transition( transform .2s ease-in-out );
            will-change: transform;
            &__button {
                @include  dimensions ( auto, 11px );
                &.left{
                    transform: rotate(180deg);
                        -webkit-transform: rotate(180deg);
                }
                &.disable{
                    &::before {
                        border: solid $color-grey;
                        border-width: 0 2px 2px 0;
                    }
                    path {
                        stroke: $color-grey;
                    }
                }
            }
            & .path {
                @include transition( all .2s ease-in-out );
            }
            &--left {
                @include transform( rotate( 180deg ) );
            }
            &--active {
                & .path {
                    fill: $color-blue;
                }
                &#{$arrowClass}  {
                    &--left {
                        &:hover {
                            @include transform( translateX( -5px ) rotate( 180deg ) );
                        }
                    }
                    &--right {
                        &:hover {
                            @include transform( translateX( 5px ) );
                        }
                    }
                }
            }
        }
    }
</style>